import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  BookOpen,
  Calendar,
  User,
  ArrowRight,
  Search,
  Tag,
} from "lucide-react";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API}/blog`);
      const data = await response.json();
      // Backend returns array directly, not wrapped in {posts: [...]}
      setPosts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter(
    (post) =>
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = [...new Set(posts.map((p) => p.category).filter(Boolean))];

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rnb-blue"></div>
      </div>
    );
  }

  return (
    <div>
      <SEO 
        title="Healthcare Practice Blog - Operations, Staffing & Growth Tips"
        description="Practical insights for clinic owners and practice administrators on improving operations, managing staff, staying compliant, choosing EHR systems, and growing sustainably."
        keywords="healthcare practice tips, how to run a medical practice, clinic management blog, healthcare operations advice, medical staff management, practice growth strategies, EHR tips for clinics"
      />
      <StructuredData data={[{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://www.rnbconsultinggroup.com/blog" }
        ]
      }]} />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-rnb-light to-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-blue/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-rnb-blue/10 rounded-full mb-6">
              <BookOpen className="w-4 h-4 text-rnb-blue mr-2" />
              <span className="text-sm font-medium text-rnb-navy">Insights & Resources</span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-rnb-navy mb-6">
              RNB Consulting Blog
            </h1>

            <p className="text-lg text-rnb-gray mb-8">
              Expert insights, industry trends, and practical advice for healthcare 
              entrepreneurs and practice owners.
            </p>

            {/* Search */}
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-rnb-gray" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6"
                data-testid="blog-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      {categories.length > 0 && (
        <section className="py-8 bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm font-medium text-rnb-gray">Categories:</span>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSearchQuery(category)}
                  className="px-4 py-1.5 bg-slate-100 hover:bg-rnb-blue/10 text-rnb-navy text-sm rounded-full transition-colors"
                >
                  {category}
                </button>
              ))}
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="px-4 py-1.5 bg-rnb-blue text-white text-sm rounded-full"
                >
                  Clear filter
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Blog Posts */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-rnb-gray/30 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-2">
                {searchQuery ? "No articles found" : "Coming Soon"}
              </h3>
              <p className="text-rnb-gray mb-6">
                {searchQuery
                  ? "Try a different search term"
                  : "We're working on bringing you valuable insights. Check back soon!"}
              </p>
              {searchQuery && (
                <Button variant="outline" onClick={() => setSearchQuery("")}>
                  Clear search
                </Button>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Card key={post.post_id} className="border-0 shadow-sm card-hover overflow-hidden">
                  {post.featured_image && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    {post.category && (
                      <div className="flex items-center gap-2 mb-3">
                        <Tag className="w-3 h-3 text-rnb-blue" />
                        <span className="text-xs font-medium text-rnb-blue uppercase tracking-wide">
                          {post.category}
                        </span>
                      </div>
                    )}
                    <h2 className="font-heading text-xl font-semibold text-rnb-navy mb-3 line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-rnb-gray text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="flex items-center gap-4 text-xs text-rnb-gray">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {post.author_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(post.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <Link to={`/blog/${post.slug}`}>
                        <Button variant="ghost" size="sm" className="text-rnb-blue hover:text-rnb-blue/80">
                          Read
                          <ArrowRight className="ml-1 h-3 w-3" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-rnb-navy mb-4">
            Want Personalized Guidance?
          </h2>
          <p className="text-rnb-gray mb-8">
            Our blog shares general insights, but your practice is unique. 
            Schedule a free consultation to discuss your specific needs.
          </p>
          <Link to="/consultation">
            <Button size="lg" className="btn-hover-lift">
              Schedule Free Consultation
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Blog;
